/**
 * pruneUnlinkedProjectSuppliers — keep only suppliers explicitly linked
 * to a project's BoM, quotes, or awards. Strips directory bulk-import
 * pollution from legacy applyBomFromUpload behaviour.
 */
import type { SupplyChainPlan, Supplier } from '../types/supplyChain';

/** Supplier ids referenced by quotes, awards, or BoM shortlists. */
export function linkedSupplierIds(plan: SupplyChainPlan): Set<string> {
  const ids = new Set<string>();
  for (const q of plan.quotes ?? []) {
    if (q.supplierId) ids.add(q.supplierId);
  }
  for (const item of plan.bom ?? []) {
    if (item.awardedSupplierId) ids.add(item.awardedSupplierId);
    for (const sid of item.shortlistedSupplierIds ?? []) {
      if (sid) ids.add(sid);
    }
  }
  return ids;
}

/**
 * Returns a new plan whose `suppliers` array contains only linked entries.
 * When `linked` is empty, returns plan unchanged (avoid wiping manual adds
 * before first quote/BoM link on fresh plans with small shortlists).
 */
export function pruneUnlinkedProjectSuppliers(plan: SupplyChainPlan): SupplyChainPlan {
  const linked = linkedSupplierIds(plan);
  if (linked.size === 0) return plan;
  const kept = plan.suppliers.filter((s) => linked.has(s.id));
  if (kept.length === plan.suppliers.length) return plan;
  return { ...plan, suppliers: kept };
}

/** True when the plan likely has directory bulk-import pollution. */
export function shouldPruneProjectSuppliers(plan: SupplyChainPlan, threshold = 50): boolean {
  return plan.suppliers.length >= threshold && linkedSupplierIds(plan).size < plan.suppliers.length;
}

export function mergeProjectSupplier(existing: Supplier[], incoming: Supplier): Supplier[] {
  if (existing.some((s) => s.id === incoming.id)) return existing;
  return [...existing, incoming];
}
